import { Component, computed, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { RealmService } from '@/app/services/realm.service';
import { CategoryCurrencyConfig, RealmConfig } from '@/lib-backend';
import { CategoryCurrencyForm } from '@/app/components/category-config/category-currency-form';
import { OverridableSection } from '@/app/components/category-config/overridable-section';

@Component({
    selector: 'app-website-info-currency',
    standalone: true,
    imports: [FormsModule, Select, Button, Dialog, CategoryCurrencyForm, OverridableSection],
    templateUrl: './currency-tab.html',
})
export class CurrencyTab {
    readonly realm = input.required<RealmConfig>();
    readonly draft = model.required<CategoryCurrencyConfig>();
    readonly overridden = model.required<boolean>();

    protected readonly realmService = inject(RealmService);

    protected readonly section = computed(() => this.realm().CategorySpecific.Currency);

    protected readonly dirty = computed(() =>
        this.overridden() !== this.section().IsOverridden ||
        JSON.stringify(this.draft()) !== JSON.stringify(this.section().Effective)
    );

    protected readonly importableRealms = computed(() =>
        this.realmService.realms().filter(r => r.RealmId !== this.realm().RealmId)
    );

    protected importDialogVisible = signal(false);
    protected importSourceRealmId = signal<string | null>(null);
    protected importLoading = signal(false);

    protected onToggle(value: boolean): void {
        this.overridden.set(value);
        if (!value) {
            this.draft.set(clone(this.section().Inherited));
        }
    }

    protected resetAll(): void {
        const s = this.section();
        this.draft.set(clone(s.Effective));
        this.overridden.set(s.IsOverridden);
    }

    protected openImportDialog(): void {
        this.realmService.loadRealms();
        this.importSourceRealmId.set(null);
        this.importDialogVisible.set(true);
    }

    protected async applyImport(): Promise<void> {
        const realmId = this.importSourceRealmId();
        if (!realmId) return;
        this.importLoading.set(true);
        try {
            const result = await this.realmService.getRealm(realmId);
            if (result) {
                this.draft.set(clone(result.config.CategorySpecific.Currency.Effective));
                this.overridden.set(true);
            }
        } finally {
            this.importLoading.set(false);
        }
        this.importDialogVisible.set(false);
    }
}

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}
